import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, CircularProgress, Box, Typography } from "@mui/material";
import { Layout } from "../../components/layout";
import {
  GiftIcon,
  MaleIcon,
  PhoneIcon,
  EmailIcon,
  RightArrowIcon,
  SadFaceIcon,
  HappyFaceIcon,
  SortIcon,
  SortDownIcon,
} from "../../components/Icons";
import {
  PageContainer,
  PatientDetailsCard,
  PatientInfoSection,
  PatientNameBlock,
  PatientDetailsLabel,
  PatientName,
  PatientDetailColumn,
  PatientDetailItem,
  PatientDetailText,
  AllergiesSection,
  AllergiesLabel,
  AllergiesLabelText,
  AllergiesText,
  NoAllergiesText,
  SectionContainer,
  SectionHeader,
  SectionTitle,
  SessionsTable,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableCell,
  ProviderCell,
  ProviderAvatar,
  ProviderName,
  ActionLink,
  NoDataText,
} from "./PatientProfilePage.styled";
import {
  patientsService,
  PatientDetails,
} from "../../services/patientsService";
import api from "../../api/axios";

const PatientProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [patient, setPatient] = useState<PatientDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchInitiatedRef = useRef(false);

  // Sorting state
  type SortField = "provider" | "date";
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Fetch patient data
  useEffect(() => {
    if (!id || fetchInitiatedRef.current) return;

    fetchInitiatedRef.current = true;
    setLoading(true);

    const patientId = Number(id);

    patientsService
      .getPatientById(patientId)
      .then((data) => {
        setPatient(data);
        api.post(`/patients/${patientId}/audit/profile-viewed`).catch((err) => {
          console.error("Error sending profile view audit:", err);
        });
      })
      .catch((err) => {
        console.error("Error fetching patient:", err);
        setError(t("patientProfile.loadError"));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, t]);

  // Format phone number to +1 (XXX) XXX-XXXX
  const formatPhone = (phone: string): string => {
    if (!phone) return "";
    const digits = phone.replace(/\D/g, "");
    const localDigits =
      digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
    if (localDigits.length === 10) {
      return `+1 (${localDigits.slice(0, 3)}) ${localDigits.slice(
        3,
        6,
      )}-${localDigits.slice(6)}`;
    }
    return phone;
  };

  const formatDate = (isoDate: string): string => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const handleSessionClick = (sessionId: number) => {
    navigate(`/sessions/${sessionId}`);
  };

  const handleTreatmentPlanClick = (treatmentPlanId: number | string) => {
    navigate(`/treatment-plan/${treatmentPlanId}`);
  };

  const handleViewAllSessions = () => {
    if (!patient) return;
    // Navigate to all sessions with patient filter applied
    navigate(`/sessions?patientId=${patient.id}`);
  };

  // Handle sort click
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Sort sessions
  const sortedSessions = useMemo(() => {
    if (!patient) return [];

    return [...patient.sessions].sort((a, b) => {
      let comparison = 0;

      if (sortField === "provider") {
        comparison = a.providerName.localeCompare(b.providerName);
      } else if (sortField === "date") {
        comparison =
          new Date(a.sessionDate).getTime() - new Date(b.sessionDate).getTime();
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [patient, sortField, sortDirection]);

  // Render sort icon
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <SortIcon style={{ opacity: 0.5 }} />;
    }
    return sortDirection === "asc" ? (
      <SortDownIcon style={{ transform: "rotate(180deg)" }} />
    ) : (
      <SortDownIcon />
    );
  };

  if (loading) {
    return (
      <Layout>
        <PageContainer>
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        </PageContainer>
      </Layout>
    );
  }

  if (error || !patient) {
    return (
      <Layout>
        <PageContainer>
          <NoDataText>{error || t("patientProfile.notFound")}</NoDataText>
        </PageContainer>
      </Layout>
    );
  }

  const hasAllergies = patient.allergies.length > 0;

  return (
    <Layout>
      <PageContainer>
        <Typography variant="h1" marginBottom="32px">
          {t("patientProfile.title")}
        </Typography>

        {/* Patient Details Card */}
        <PatientDetailsCard>
          <PatientInfoSection>
            <PatientNameBlock>
              <PatientDetailsLabel>
                {t("patientProfile.patientDetails")}:
              </PatientDetailsLabel>
              <PatientName>{patient.basicInfo.fullName}</PatientName>
            </PatientNameBlock>

            <PatientDetailColumn>
              <PatientDetailItem>
                <GiftIcon />
                <PatientDetailText>
                  {formatDate(patient.basicInfo.dateOfBirth)}
                </PatientDetailText>
              </PatientDetailItem>
              <PatientDetailItem>
                <MaleIcon />
                <PatientDetailText>
                  {patient.basicInfo.gender}
                </PatientDetailText>
              </PatientDetailItem>
            </PatientDetailColumn>

            <PatientDetailColumn>
              <PatientDetailItem>
                <PhoneIcon />
                <PatientDetailText>
                  {formatPhone(patient.basicInfo.phone)}
                </PatientDetailText>
              </PatientDetailItem>
              <PatientDetailItem>
                <EmailIcon />
                <PatientDetailText>{patient.basicInfo.email}</PatientDetailText>
              </PatientDetailItem>
            </PatientDetailColumn>
          </PatientInfoSection>

          <AllergiesSection hasAllergies={hasAllergies}>
            <AllergiesLabel>
              {hasAllergies ? (
                <SadFaceIcon fill="#EF5350" width={16} height={16} />
              ) : (
                <HappyFaceIcon fill="#296C50" width={16} height={16} />
              )}
              <AllergiesLabelText hasAllergies={hasAllergies}>
                {t("patientProfile.allergies")}:
              </AllergiesLabelText>
            </AllergiesLabel>
            {hasAllergies ? (
              <AllergiesText>{patient.allergies.join(", ")}</AllergiesText>
            ) : (
              <NoAllergiesText>
                {t("patientProfile.noAllergies")}
              </NoAllergiesText>
            )}
          </AllergiesSection>
        </PatientDetailsCard>

        {/* Sessions Section */}
        <SectionContainer>
          <SectionHeader>
            <SectionTitle>
              {t("patientProfile.sessions")} ({patient.sessions.length})
            </SectionTitle>
            <Button
              variant="outlined"
              onClick={handleViewAllSessions}
              endIcon={<RightArrowIcon fill="currentColor" />}
            >
              {t("patientProfile.viewAllSessions")}
            </Button>
          </SectionHeader>

          <SessionsTable>
            <TableHeader>
              <TableHeaderCell
                onClick={() => handleSort("provider")}
                style={{ cursor: "pointer" }}
              >
                {t("patientProfile.provider")}
                {renderSortIcon("provider")}
              </TableHeaderCell>
              <TableHeaderCell
                onClick={() => handleSort("date")}
                style={{ cursor: "pointer" }}
              >
                {t("patientProfile.date")}
                {renderSortIcon("date")}
              </TableHeaderCell>
              <TableHeaderCell>{t("patientProfile.actions")}</TableHeaderCell>
            </TableHeader>

            {sortedSessions.length > 0 ? (
              sortedSessions.map((session) => (
                <TableRow key={session.sessionId}>
                  <TableCell>
                    <ProviderCell>
                      <ProviderAvatar>
                        {session.providerName.charAt(0)}
                      </ProviderAvatar>
                      <ProviderName
                        onClick={
                          session.providerId
                            ? () =>
                                navigate(
                                  `/user-management/${session.providerId}`,
                                )
                            : undefined
                        }
                        sx={
                          session.providerId
                            ? { cursor: "pointer", "&:hover": { textDecoration: "underline" } }
                            : undefined
                        }
                      >
                        {session.providerName}
                      </ProviderName>
                    </ProviderCell>
                  </TableCell>
                  <TableCell>{formatDate(session.sessionDate)}</TableCell>
                  <TableCell>
                    <ActionLink
                      onClick={() => handleSessionClick(session.sessionId)}
                    >
                      {t("patientProfile.sessionDetails")}
                    </ActionLink>
                    {session.treatmentPlanId && (
                      <ActionLink
                        onClick={() =>
                          handleTreatmentPlanClick(session.treatmentPlanId)
                        }
                      >
                        {t("patientProfile.treatmentPlan")}
                        <RightArrowIcon fill="#3A8AB9" width={14} height={14} />
                      </ActionLink>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <NoDataText>{t("patientProfile.noSessions")}</NoDataText>
            )}
          </SessionsTable>
        </SectionContainer>
      </PageContainer>
    </Layout>
  );
};

export default PatientProfilePage;
